/**
 * How an arena wants its picture finished (step 98), as data, and the check
 * that keeps the data honest.
 *
 * Pure — no three.js — so a bad number in an arena's JSON fails a unit test
 * rather than turning a room white on a phone. `post.js` reads what this
 * resolves; nothing else does.
 *
 *   look.post.bloom     threshold  linear brightness a pixel has to pass (1 is white)
 *                       knee       how softly it eases over the threshold
 *                       strength   how much of the glow is added back
 *                       levels     how far it spreads: each level is half the size
 *                       spread     how much each wider level adds to the one above
 *   look.post.grade     exposure, saturation, contrast; lift and gain as [r, g, b]
 *   look.post.vignette  amount (0 none, 1 black corners), softness
 */

export const LOOK_DEFAULTS = Object.freeze({
  bloom: Object.freeze({ threshold: 1, knee: 0.5, strength: 0.6, levels: 5, spread: 1 }),
  grade: Object.freeze({ exposure: 1, saturation: 1, contrast: 1, lift: [0, 0, 0], gain: [1, 1, 1] }),
  vignette: Object.freeze({ amount: 0, softness: 0.4 }),
});

export class LookError extends Error {}

const RANGES = {
  bloom: { threshold: [0, 10], knee: [0, 5], strength: [0, 4], levels: [0, 6], spread: [0, 2] },
  grade: { exposure: [0.1, 4], saturation: [0, 2], contrast: [0.5, 2] },
  vignette: { amount: [0, 1], softness: [0.01, 1] },
};
const TRIPLES = { lift: [-0.2, 0.2], gain: [0.5, 1.5] };

/** Throws a `LookError` naming the first value out of range. */
export function validateLook(post = {}, where = 'look.post') {
  for (const [group, fields] of Object.entries(post)) {
    if (!RANGES[group]) throw new LookError(`${where}: no such part "${group}"`);
    for (const [field, value] of Object.entries(fields ?? {})) {
      const triple = group === 'grade' ? TRIPLES[field] : null;
      if (triple) {
        if (!Array.isArray(value) || value.length !== 3 || value.some((v) => !(v >= triple[0] && v <= triple[1]))) {
          throw new LookError(`${where}.${group}.${field}: three numbers in ${triple[0]}..${triple[1]}`);
        }
        continue;
      }
      const range = RANGES[group][field];
      if (!range) throw new LookError(`${where}.${group}: no such setting "${field}"`);
      if (!(typeof value === 'number' && value >= range[0] && value <= range[1])) {
        throw new LookError(`${where}.${group}.${field}: ${value} is outside ${range[0]}..${range[1]}`);
      }
    }
  }
  return post;
}

/** The arena's settings over the defaults, part by part. */
export function resolveLook(post = {}) {
  validateLook(post);
  return {
    bloom: { ...LOOK_DEFAULTS.bloom, ...(post.bloom ?? {}) },
    grade: { ...LOOK_DEFAULTS.grade, ...(post.grade ?? {}) },
    vignette: { ...LOOK_DEFAULTS.vignette, ...(post.vignette ?? {}) },
  };
}
